/**
 * PersonCard — Task 8
 * Mirrors FOM posting.html personCard() exactly.
 * Used for both supervisors and officers.
 *
 * Behavior parity with latest FOM posting.html:
 *   - Break "End Time" auto-populates from "Time" via calcBreakEnd() in the
 *     store's updateBreak() action. The user can still type to override.
 *   - Break "Covered By" select is limited to personnel on duty in the
 *     active posting (supervisors + officers with a call sign), excluding
 *     the current entry, plus an explicit "Self" option.
 */
import { usePostingStore, SECONDARY_TASKS, PRIMARY_POSTS, loadRoster, type PersonEntry } from '../../stores/posting'
import { markDirty } from '../../stores/sync'

function categoryFor(cs: string): 'Supervisor' | 'Officer' | 'Developer' | 'Exclude' {
  if (!cs) return 'Officer'
  if (cs.startsWith('DV-')) return 'Developer'
  if (cs.startsWith('SC-') || cs.startsWith('E-') || cs.startsWith('M-') ||
      cs.startsWith('L-') || cs.startsWith('O-') || cs.startsWith('P-')) return 'Exclude'
  const m = cs.match(/^S-(\d+)$/)
  if (m) { const n = parseInt(m[1]); return (n >= 1 && n <= 8) ? 'Supervisor' : 'Officer' }
  return 'Officer'
}

const inp: React.CSSProperties = {
  background: 'var(--bg-deep)', border: '1px solid var(--border)', borderRadius: 6,
  color: 'var(--text-primary)', fontSize: 12, padding: '5px 8px', outline: 'none',
  boxSizing: 'border-box', width: '100%',
}

const sel: React.CSSProperties = { ...inp, cursor: 'pointer' }

interface Props {
  entry: PersonEntry
  index: number
  type: 'sup' | 'ofc'
  shift: string
}

function ln(name: string) {
  if (!name) return ''
  if (name.includes(',')) return name.split(',')[0].trim()
  const parts = name.trim().split(/\s+/)
  return parts[parts.length - 1] ?? ''
}

export default function PersonCard({ entry, index, type, shift }: Props) {
  const store = usePostingStore()
  const roster = loadRoster()

  // Active-tab data for the "Covered By" on-duty list
  const active = store.activeData()

  function update(patch: Partial<PersonEntry>) {
    markDirty()
    type === 'sup' ? store.updateSup(index, patch) : store.updateOfc(index, patch)
  }

  const rosterEntry = roster.find(r => r.callSign === entry.cs)
  const displayName = rosterEntry?.name ?? ''

  // On-duty list = supervisors + officers that have a cs filled in,
  // minus the current entry. Matches FOM posting.html allOnDuty().
  const onDuty = [...active.supervisors, ...active.officers]
    .filter(p => p.cs && p.cs !== entry.cs)

  const POST_COLORS: Record<string, string> = {
    Vehicle: 'var(--accent)', Mall: '#60a5fa', Desk: '#a78bfa', Special: '#22c55e',
  }

  return (
    <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 12, marginBottom: 8 }}>

      {/* Row 1: Call sign select | Shift time | Done (if selected) | Remove */}
      <div style={{ display: 'grid', gridTemplateColumns: `1fr 110px${entry.cs ? ' 60px' : ''} 28px`, gap: 6, marginBottom: 6, alignItems: 'center' }}>
        <select value={entry.cs} onChange={e => { markDirty(); store.pickCS(type, index, e.target.value, shift) }}
          style={{ ...sel, fontWeight: 800, color: '#fbbf24' }}>
          <option value="">Select</option>
          {(() => {
            const sups = roster.filter(r => categoryFor(r.callSign) === 'Supervisor')
            const ofcs = roster.filter(r => categoryFor(r.callSign) === 'Officer')
            const devs = roster.filter(r => categoryFor(r.callSign) === 'Developer')
            const makeOpt = (r: { callSign: string; name: string }) => (
              <option key={r.callSign || r.name} value={r.callSign || r.name}>
                {r.callSign ? `${r.callSign}${r.name ? ' — ' + ln(r.name) : ''}` : r.name}
              </option>
            )
            if (type === 'sup') {
              return [
                sups.length > 0 && <optgroup key="sups" label="── Supervisors ──">{sups.map(makeOpt)}</optgroup>,
                devs.length > 0 && <optgroup key="devs" label="── Developer ──">{devs.map(makeOpt)}</optgroup>,
              ]
            }
            return [
              ofcs.length > 0 && <optgroup key="ofcs" label="── Officers ──">{ofcs.map(makeOpt)}</optgroup>,
            ]
          })()}
        </select>

        <input value={entry.time} onChange={e => update({ time: e.target.value })}
          placeholder="Shift time" style={inp} />

        {/* Done button — only shows after a person is selected */}
        {entry.cs && (
          <button
            onClick={() => { /* selection already committed on change — just visual confirmation */ }}
            style={{
              background: 'rgba(34,197,94,0.15)',
              border: '1px solid rgba(34,197,94,0.4)',
              borderRadius: 6,
              color: '#22c55e',
              fontSize: 11,
              fontWeight: 800,
              padding: '5px 4px',
              cursor: 'default',
              textAlign: 'center',
            }}
          >✓ Done</button>
        )}

        <button onClick={() => type === 'sup' ? store.removeSup(index) : store.removeOfc(index)}
          style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 16, cursor: 'pointer', padding: 0 }}>
          ✕
        </button>
      </div>

      {/* Row 2: Name display — below select and time */}
      <div style={{ fontSize: 13, color: 'var(--text-primary)', padding: '5px 8px', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border)', borderRadius: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 8 }}>
        {displayName || <span style={{ color: 'var(--text-hint)' }}>Name will appear here after selection</span>}
      </div>

      {/* OIC checkbox */}
      <div style={{ marginBottom: 8 }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: '4px 10px', borderRadius: 8, border: `1px solid ${entry.isOIC ? 'var(--accent)' : 'var(--border)'}`, background: entry.isOIC ? 'rgba(201,168,76,0.15)' : 'rgba(255,255,255,0.03)' }}>
          <input type="checkbox" checked={entry.isOIC}
            onChange={e => update({ isOIC: e.target.checked })}
            style={{ accentColor: 'var(--accent)' }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: entry.isOIC ? 'var(--accent)' : 'var(--text-muted)' }}>⭐ Assigned as OIC</span>
        </label>
      </div>

      {/* Primary Posts */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, marginBottom: 5 }}>Primary Posts</div>
        {entry.posts.map((p, pi) => {
          const color = POST_COLORS[p.post] ?? 'var(--text-muted)'
          return (
            <div key={pi} style={{ display: 'grid', gridTemplateColumns: '100px 1fr 10px 1fr 24px', gap: 5, marginBottom: 4, alignItems: 'center' }}>
              <select value={p.post} onChange={e => store.updatePost(type, index, pi, 'post', e.target.value)}
                style={{ ...sel, color }}>
                {PRIMARY_POSTS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
              <input value={p.from} onChange={e => store.updatePost(type, index, pi, 'from', e.target.value)}
                placeholder="From" style={{ ...inp, textAlign: 'center' }} />
              <span style={{ color: 'var(--text-hint)', fontSize: 11, textAlign: 'center' }}>→</span>
              <input value={p.to} onChange={e => store.updatePost(type, index, pi, 'to', e.target.value)}
                placeholder="To" style={{ ...inp, textAlign: 'center' }} />
              <button onClick={() => store.removePost(type, index, pi)}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer', padding: 0 }}>✕</button>
            </div>
          )
        })}
        <button onClick={() => store.addPost(type, index)}
          style={{ padding: '3px 8px', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 5, color: 'var(--accent)', fontSize: 11, cursor: 'pointer', marginTop: 2 }}>
          + Add Post
        </button>
      </div>

      {/* Secondary Tasks */}
      <div style={{ borderTop: '1px solid rgba(30,58,95,0.5)', paddingTop: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, marginBottom: 5 }}>📌 Secondary Tasks</div>
        {entry.tasks.map((t, ti) => (
          <div key={ti} style={{ display: 'grid', gridTemplateColumns: '1fr 100px 24px', gap: 5, marginBottom: 4, alignItems: 'center' }}>
            <select value={t.taskId} onChange={e => store.updateTask(type, index, ti, 'taskId', e.target.value)} style={sel}>
              <option value="">— Task —</option>
              {SECONDARY_TASKS.map(st => <option key={st.id} value={st.id}>{st.label}</option>)}
            </select>
            <input value={t.time} onChange={e => store.updateTask(type, index, ti, 'time', e.target.value)}
              placeholder="Time" style={inp} />
            <button onClick={() => store.removeTask(type, index, ti)}
              style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer', padding: 0 }}>✕</button>
          </div>
        ))}
        <button onClick={() => store.addTask(type, index)}
          style={{ padding: '3px 8px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 5, color: '#60a5fa', fontSize: 11, cursor: 'pointer', marginTop: 2 }}>
          + Task
        </button>
      </div>

      {/* Break */}
      <div style={{ borderTop: '1px solid rgba(30,58,95,0.5)', paddingTop: 8 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, marginBottom: 5 }}>☕ Break</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-hint)', marginBottom: 2 }}>Time</div>
            <input value={entry.brk.time} onChange={e => store.updateBreak(type, index, 'time', e.target.value)}
              placeholder="e.g. 1200" style={inp} />
          </div>
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-hint)', marginBottom: 2 }}>End Time</div>
            <input value={entry.brk.endTime} onChange={e => store.updateBreak(type, index, 'endTime', e.target.value)}
              placeholder="auto +30m" style={inp} />
          </div>
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-hint)', marginBottom: 2 }}>Covered By</div>
            <select value={entry.brk.coveredBy} onChange={e => store.updateBreak(type, index, 'coveredBy', e.target.value)} style={sel}>
              <option value="">—</option>
              <option value="Self">Self</option>
              {onDuty.map(p => (
                <option key={p.cs} value={p.cs}>
                  {p.cs}{p.name ? ' — ' + ln(p.name) : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  )
}
